/**
 * Versioned localStorage. Everything the app owns lives under `ala.v1.*`.
 *
 * Storage is split by slice rather than one blob so that a quota error while
 * saving (say) a long wiki page can't take the calendar down with it, and so a
 * corrupt slice degrades to its default instead of wiping the whole app.
 */

const PREFIX = 'ala.v1.'

export function load<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(PREFIX + key)
    if (raw === null) return fallback
    const parsed = JSON.parse(raw) as T
    return parsed ?? fallback
  } catch {
    return fallback
  }
}

export function save(key: string, value: unknown): boolean {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify(value))
    return true
  } catch {
    // Quota exceeded or storage disabled (private browsing). The app keeps
    // working from memory for this session; the caller surfaces the warning.
    return false
  }
}

export function clearAll(): void {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(PREFIX))
      .forEach((k) => localStorage.removeItem(k))
  } catch {
    /* nothing sensible to do */
  }
}

/** Approximate bytes used, for the storage readout in Settings. */
export function usedBytes(): number {
  try {
    return Object.keys(localStorage)
      .filter((k) => k.startsWith(PREFIX))
      .reduce((n, k) => n + k.length + (localStorage.getItem(k)?.length ?? 0), 0)
  } catch {
    return 0
  }
}
